import React, { FC, useMemo } from 'react';
import { Space } from 'antd';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import Section from '~/layouts/Section ';
import { Form, Tabs } from '~/components';
import { BusinessIcon, IndividualsIcon } from '~/img';
import { useGetApplication, useGetPayloadWithProvided } from '~/controllers/wizard';
import { getStagePayload } from '~/utils/getStagePayload';
import { KsaShareholders } from '~/types/ksa/KsaShareholders';
import IndividualReviewTableForm from './individuals/IndividualReviewTableForm';
import LegalEntitiesReviewTableForm from './legalEntities/LegalEntitiesReviewTableForm';
import BeneficialOwnerReviewTableForm from './beneficialOwners/BeneficialOwnerReviewTableForm';
import BeneficialOwnerReviewAdditionalInformation from './beneficialOwners/BeneficialOwnerReviewAdditionalInformation';

const ShareTables: FC = () => {
  const { t } = useTranslation('ksaReview');

  const applicationQuery = useGetApplication();
  const payload = useGetPayloadWithProvided();
  const stagePayload = getStagePayload.wizard(applicationQuery.data);
  const errorFields = stagePayload?.activeStep.errorInformation?.errorFields;
  const individualHasWarnings = useMemo(() => {
    if (!errorFields) return false;
    return !!errorFields.find((errorObj) =>
      Object.keys(errorObj).find((namePath) => namePath.includes('individuals'))
    );
  }, [errorFields]);
  const legalEntitiesHasWarnings = useMemo(() => {
    if (!errorFields) return false;
    return !!errorFields.find((errorObj) =>
      Object.keys(errorObj).find(
        (namePath) => namePath.includes('legalEntities') || namePath.includes('beneficialOwners')
      )
    );
  }, [errorFields]);

  const shareholders = payload?.shareholders as KsaShareholders;
  const isIndividualsTabDisabled = isEmpty(shareholders?.individuals);
  const isLegalEntityTabDisabled = isEmpty(shareholders?.legalEntities);

  return (
    <>
      <StyledTabs
        sticky
        centered
        defaultActiveKey={isIndividualsTabDisabled ? legalEntityTabKey : individualsTabKey}
      >
        <StyledTabs.TabPane
          tab={
            <span data-warning={individualHasWarnings.toString()}>
              <IndividualsIcon />
              {t('Individual shareholders')}
            </span>
          }
          disabled={isIndividualsTabDisabled}
          key={individualsTabKey}
        >
          <div className="ShareTables-TabContent">
            <Section>
              <Form.Item name={['reviewShareholders']} noStyle>
                <IndividualReviewTableForm />
              </Form.Item>
            </Section>
          </div>
        </StyledTabs.TabPane>
        <StyledTabs.TabPane
          tab={
            <span data-warning={legalEntitiesHasWarnings.toString()}>
              <BusinessIcon />
              {t('Legal entity shareholders')}
            </span>
          }
          key={legalEntityTabKey}
          disabled={isLegalEntityTabDisabled}
        >
          <div className="ShareTables-TabContent">
            <Space size={56} direction="vertical" style={fullWidth}>
              <Form.Item name={['reviewShareholders']} noStyle>
                <LegalEntitiesReviewTableForm />
              </Form.Item>
              <Form.Item name={['reviewShareholders']} noStyle>
                <BeneficialOwnerReviewTableForm />
              </Form.Item>
              <BeneficialOwnerReviewAdditionalInformation />
            </Space>
          </div>
        </StyledTabs.TabPane>
      </StyledTabs>
    </>
  );
};

const individualsTabKey = 'individualsTabKey';
const legalEntityTabKey = 'legalEntityTabKey';

const fullWidth = { width: '100%' };

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 24px;
    z-index: 3;
  }

  .ant-table-empty {
    tbody {
      display: none;
    }
  }

  .ShareTables-TabContent {
    max-width: calc(100vw - ${16 * 2 + 8 * 2}px);
  }
`;

export default ShareTables;
